import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { Card, Form, Input, Checkbox, Upload, Button, DatePicker, Space, Typography, message, Tabs, Slider, Radio, Row, Col, Spin, Select, Switch } from 'antd';
import { UploadOutlined, RobotOutlined, ScissorOutlined, SoundOutlined, PictureOutlined, VideoCameraOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { createPost } from '../api/posts';
import { generateCaption } from '../api/insights';
import { usePermissions } from '../../../hooks/usePermissions';
import Cropper from 'react-easy-crop';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';
import { Canvas, IText, Image as FabricImage } from 'fabric';



const { TextArea } = Input;
const { Title, Text } = Typography;

const PLATFORM_OPTIONS = [
  { label: 'Facebook', value: 'facebook' },
  { label: 'LinkedIn', value: 'linkedin' },
  { label: 'X', value: 'x' },
  { label: 'Instagram', value: 'instagram' },
];

// Utility: create image from url
const createImage = (url) => new Promise((resolve, reject) => {
  const img = new Image();
  img.addEventListener('load', () => resolve(img));
  img.addEventListener('error', (error) => reject(error));
  img.setAttribute('crossOrigin', 'anonymous');
  img.src = url;
});

async function getCroppedImg(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      resolve({ blob, url });
    }, 'image/jpeg');
  });
}

const loadFFmpeg = (() => {
  let ffmpegInstance = null;
  return async () => {
    if (ffmpegInstance) return ffmpegInstance;
    const ffmpeg = new FFmpeg({ log: false });
    await ffmpeg.load();
    ffmpegInstance = ffmpeg;
    return ffmpeg;
  };
})();

const PostComposer = () => {
  const { canViewField, canEditField } = usePermissions();

  const canFieldContentShow = canEditField('smm', 'content') || canViewField('smm', 'content');
  const canFieldContentEdit = canEditField('smm', 'content');
  const canFieldPlatformsShow = canEditField('smm', 'platforms') || canViewField('smm', 'platforms');
  const canFieldPlatformsEdit = canEditField('smm', 'platforms');
  const canFieldMediaShow = canEditField('smm', 'mediaUrls') || canViewField('smm', 'mediaUrls');
  const canFieldMediaEdit = canEditField('smm', 'mediaUrls');
  const canFieldScheduleShow = canEditField('smm', 'scheduledTime') || canViewField('smm', 'scheduledTime');
  const canFieldScheduleEdit = canEditField('smm', 'scheduledTime');

  const [form] = Form.useForm();
  const [mediaList, setMediaList] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  // Media editor state
  const [selectedIndex, setSelectedIndex] = useState(null);
  const selectedItem = mediaList[selectedIndex ?? -1];
  const selectedType = selectedItem?.type || selectedItem?.originFileObj?.type || '';
  const isImage = selectedType.startsWith('image');
  const isVideo = selectedType.startsWith('video');

  // Image cropper state
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [aspect, setAspect] = useState(1); // 1:1 default

  const cssAspect = useMemo(() => {
    if (Math.abs(aspect - 1) < 1e-6) return '1 / 1';
    if (Math.abs(aspect - 9 / 16) < 1e-6) return '9 / 16';
    if (Math.abs(aspect - 16 / 9) < 1e-6) return '16 / 9';
    return '1 / 1';
  }, [aspect]);

  const onCropComplete = useCallback((_, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  // Video editor state
  const [trimRange, setTrimRange] = useState([0, 5]);
  const [duration, setDuration] = useState(0);
  const [audioFile, setAudioFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const videoRef = useRef(null);

  // Fabric.js canvas for overlays
  const canvasRef = useRef(null);
  const editorRef = useRef(null);
  const [fabricCanvas, setFabricCanvas] = useState(null);
  const [filter, setFilter] = useState('none');
  const [overlayMap, setOverlayMap] = useState({}); // index -> fabric JSON
  const [originalImageMap, setOriginalImageMap] = useState({}); // index -> original image URL
  const [overlayEditMode, setOverlayEditMode] = useState(true);

  // Text style controls
  const [textColor, setTextColor] = useState('#ffffff');
  const [textSize, setTextSize] = useState(28);

  // Video options
  const [speed, setSpeed] = useState(1);
  const [muteOriginal, setMuteOriginal] = useState(false);
  const [musicVolume, setMusicVolume] = useState(1);
  const [resolution, setResolution] = useState('auto');
  const [videoZoom, setVideoZoom] = useState(1);
  const [videoOffset, setVideoOffset] = useState({ x: 0, y: 0 });
  const [isDraggingMedia, setIsDraggingMedia] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const startOffset = useRef({ x: 0, y: 0 });

  const onPointerDownMedia = (e) => {
    if (overlayEditMode) return; // Only allow media dragging when not in overlay mode
    setIsDraggingMedia(true);
    dragStart.current = { x: e.clientX, y: e.clientY };
    startOffset.current = { ...videoOffset };
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
  };
  
  const onPointerMoveMedia = (e) => {
    if (!isDraggingMedia || overlayEditMode) return;
    e.preventDefault();
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    setVideoOffset({ 
      x: startOffset.current.x + dx, 
      y: startOffset.current.y + dy 
    });
  };
  
  const onPointerUpMedia = (e) => {
    if (!isDraggingMedia) return;
    setIsDraggingMedia(false);
    e.preventDefault();
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
  };

  const initCanvas = useCallback(() => {
    if (!canvasRef.current) return;
    const c = new Canvas(canvasRef.current, { 
      preserveObjectStacking: true, 
      selection: true,
      width: 400,
      height: 400
    });
    setFabricCanvas(c);
    
    // Set initial canvas size
    setTimeout(() => {
      if (editorRef.current) {
        const { clientWidth, clientHeight } = editorRef.current;
        if (clientWidth && clientHeight) {
          c.setWidth(clientWidth);
          c.setHeight(clientHeight);
          c.renderAll();
        }
      }
    }, 100);
  }, []);

  const addText = () => {
    if (!fabricCanvas) {
      messageApi.error('Canvas not ready. Please wait a moment and try again.');
      return;
    }
    const text = new IText('Your text', { 
      left: 50, 
      top: 50, 
      fill: textColor, 
      fontSize: textSize, 
      shadow: 'rgba(0,0,0,0.3) 2px 2px 4px',
      fontFamily: 'Arial',
      editable: true
    });
    fabricCanvas.add(text);
    fabricCanvas.setActiveObject(text);
    fabricCanvas.renderAll();
    messageApi.success('Text added! Click on the text to edit it.');
  };

  const addSticker = async (url) => {
    if (!fabricCanvas) {
      messageApi.error('Canvas not ready. Please wait a moment and try again.');
      return;
    }
    try {
      const img = await FabricImage.fromURL(url, { crossOrigin: 'anonymous' });
      img.set({ 
        left: 100, 
        top: 100, 
        scaleX: 0.3, 
        scaleY: 0.3,
        selectable: true,
        evented: true
      });
      fabricCanvas.add(img);
      fabricCanvas.setActiveObject(img);
      fabricCanvas.renderAll();
      messageApi.success('Sticker added! You can drag and resize it.');
    } catch (e) {
      console.error('Sticker error:', e);
      messageApi.error('Failed to add sticker');
    }
  };

  const clearOverlays = () => {
    if (!fabricCanvas) {
      messageApi.error('Canvas not ready');
      return;
    }
    fabricCanvas.clear();
    fabricCanvas.renderAll();
    messageApi.success('Overlays cleared');
  };

  const saveOverlay = () => {
    if (!fabricCanvas || selectedIndex == null) {
      messageApi.error('No canvas or media selected');
      return;
    }
    const json = fabricCanvas.toJSON();
    setOverlayMap((m) => ({ ...m, [selectedIndex]: json }));
    messageApi.success('Overlay saved for this media item');
  };

  const applyTextStyle = () => {
    if (!fabricCanvas) return;
    const active = fabricCanvas.getActiveObject();
    if (active && active.type === 'i-text') {
      active.set({ fill: textColor, fontSize: textSize });
      fabricCanvas.requestRenderAll();
    } else {
      messageApi.info('Select a text layer');
    }
  };

  const uploadStickerProps = {
    accept: 'image/*',
    maxCount: 1,
    beforeUpload: (file) => {
      const url = URL.createObjectURL(file);
      addSticker(url);
      messageApi.success('Sticker added');
      return false;
    },
  };

  const renderOverlayOnCanvas = async (baseCanvas) => {
    if (!fabricCanvas) return baseCanvas;
    const overlay = document.createElement('canvas');
    overlay.width = baseCanvas.width; overlay.height = baseCanvas.height;
    const exportCanvas = new Canvas(overlay);
    exportCanvas.setWidth(baseCanvas.width);
    exportCanvas.setHeight(baseCanvas.height);
    // clone objects from editor canvas
    const json = fabricCanvas.toJSON();
    await exportCanvas.loadFromJSON(json);
    // scale overlay objects from editor size to output size
    const sx = baseCanvas.width / (fabricCanvas.getWidth() || 1);
    const sy = baseCanvas.height / (fabricCanvas.getHeight() || 1);
    exportCanvas.getObjects().forEach((obj) => {
      obj.set({
        left: (obj.left || 0) * sx,
        top: (obj.top || 0) * sy,
        scaleX: (obj.scaleX || 1) * sx,
        scaleY: (obj.scaleY || 1) * sy,
      });
      obj.setCoords();
    });
    exportCanvas.renderAll();
    const ctx = baseCanvas.getContext('2d');
    ctx.drawImage(overlay, 0, 0);
    return baseCanvas;
  };

  const applyImageFilter = async () => {
    if (!isImage || !selectedItem || selectedIndex == null) return;
    
    try {
      // Store original image if not already stored
      if (!originalImageMap[selectedIndex]) {
        setOriginalImageMap(prev => ({
          ...prev,
          [selectedIndex]: selectedItem.url
        }));
      }
      
      // Use original image as base for filter application
      const baseImageUrl = originalImageMap[selectedIndex] || selectedItem.url;
      
      // If filter is 'none', revert to original
      if (filter === 'none') {
        const updated = [...mediaList];
        updated[selectedIndex] = {
          ...updated[selectedIndex],
          url: baseImageUrl,
          originFileObj: updated[selectedIndex].originFileObj // Keep original file object if exists
        };
        setMediaList(updated);
        messageApi.success('Reverted to original image');
        return;
      }
      
      // Apply filter to original image with current crop settings
      const cropArea = croppedAreaPixels || { x: 0, y: 0, width: 600, height: 600 };
      const { url, blob } = await getCroppedImg(baseImageUrl, cropArea);
      const img = await createImage(url);
      const canvas = document.createElement('canvas');
      canvas.width = img.width; 
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      
      // Apply CSS filter
      ctx.filter = filter;
      ctx.drawImage(img, 0, 0);
      
      // Apply overlays on top of filtered image
      const withOverlay = await renderOverlayOnCanvas(canvas);
      const outBlob = await new Promise((resolve) => withOverlay.toBlob(resolve, 'image/jpeg'));
      const outUrl = URL.createObjectURL(outBlob);
      
      const updated = [...mediaList];
      updated[selectedIndex] = { 
        ...updated[selectedIndex], 
        url: outUrl, 
        originFileObj: new File([outBlob], 'image.jpg', { type: 'image/jpeg' }) 
      };
      setMediaList(updated);
      messageApi.success(`Filter "${filter}" applied`);
    } catch (e) {
      console.error('Filter error:', e);
      messageApi.error('Filter failed');
    }
  };

  const resetToOriginal = () => {
    if (!isImage || selectedIndex == null || !originalImageMap[selectedIndex]) {
      messageApi.error('No original image to restore');
      return;
    }
    
    const updated = [...mediaList];
    updated[selectedIndex] = {
      ...updated[selectedIndex],
      url: originalImageMap[selectedIndex],
      originFileObj: updated[selectedIndex].originFileObj // Keep original file object
    };
    setMediaList(updated);
    setFilter('none');
    messageApi.success('Restored to original image');
  };

  const applyImageCrop = async () => {
    if (!isImage || selectedIndex == null || !croppedAreaPixels) return;
    
    try {
      // Store original image if not already stored
      if (!originalImageMap[selectedIndex]) {
        setOriginalImageMap(prev => ({
          ...prev,
          [selectedIndex]: selectedItem.url
        }));
      }
      
      // Use original image as base for cropping
      const baseImageUrl = originalImageMap[selectedIndex] || selectedItem.url;
      const { url, blob } = await getCroppedImg(baseImageUrl, croppedAreaPixels);
      const baseImg = await createImage(url);
      const canvas = document.createElement('canvas');
      canvas.width = baseImg.width; 
      canvas.height = baseImg.height;
      const ctx = canvas.getContext('2d');
      
      // Apply current filter if any
      if (filter && filter !== 'none') {
        ctx.filter = filter;
      }
      ctx.drawImage(baseImg, 0, 0);
      
      // Apply overlays on top
      const composed = await renderOverlayOnCanvas(canvas);
      const outBlob = await new Promise((resolve) => composed.toBlob(resolve, 'image/jpeg'));
      const outUrl = URL.createObjectURL(outBlob);
      
      const updated = [...mediaList];
      updated[selectedIndex] = {
        ...updated[selectedIndex],
        url: outUrl,
        originFileObj: new File([outBlob], updated[selectedIndex].name || 'image.jpg', { type: 'image/jpeg' }),
      };
      setMediaList(updated);
      messageApi.success('Image cropped and processed');
    } catch (e) {
      console.error('Crop error:', e);
      messageApi.error('Crop failed');
    }
  };

  const syncCanvasSize = useCallback(() => {
    if (!fabricCanvas || !editorRef.current) return;
    const { clientWidth, clientHeight } = editorRef.current;
    if (clientWidth && clientHeight) {
      fabricCanvas.setWidth(clientWidth);
      fabricCanvas.setHeight(clientHeight);
      fabricCanvas.renderAll();
      console.log('Canvas resized to:', clientWidth, 'x', clientHeight);
    }
  }, [fabricCanvas]);

  useEffect(() => {
    if (!fabricCanvas || !editorRef.current) return;
    
    // Initial size sync
    syncCanvasSize();
    
    // Set up resize observer
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(syncCanvasSize);
    });
    ro.observe(editorRef.current);
    
    // Also sync on window resize
    const handleResize = () => {
      requestAnimationFrame(syncCanvasSize);
    };
    window.addEventListener('resize', handleResize);
    
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [fabricCanvas, syncCanvasSize]);

  useEffect(() => {
    if (!canvasRef.current) return;
    
    // Cleanup existing canvas
    if (fabricCanvas) {
      try { 
        fabricCanvas.dispose(); 
      } catch (e) {
        console.log('Canvas disposal error:', e);
      }
    }
    
    // Initialize new canvas after a short delay to ensure DOM is ready
    const timer = setTimeout(() => {
      initCanvas();
    }, 100);
    
    return () => clearTimeout(timer);
  }, [selectedIndex, isImage, isVideo, initCanvas]);

  useEffect(() => {
    if (!fabricCanvas) return;
    
    // Configure canvas interaction based on overlay edit mode
    fabricCanvas.selection = overlayEditMode;
    fabricCanvas.skipTargetFind = !overlayEditMode;
    fabricCanvas.discardActiveObject();
    
    // Enable/disable object interactions
    fabricCanvas.getObjects().forEach(obj => {
      obj.selectable = overlayEditMode;
      obj.evented = overlayEditMode;
    });
    
    fabricCanvas.requestRenderAll();
    
    console.log('Overlay edit mode:', overlayEditMode ? 'ON (can edit overlays)' : 'OFF (can drag media)');
  }, [overlayEditMode, fabricCanvas]);

  // Auto-save overlays when switching selected media
  useEffect(() => {
    if (selectedIndex == null) return;
    return () => {
      if (fabricCanvas) {
        setOverlayMap((m) => ({ ...m, [selectedIndex]: fabricCanvas.toJSON() }));
      }
    };
  }, [selectedIndex, fabricCanvas]);

  // Handlers (declared before items)
  function onMediaChange({ fileList: newList }) {
    const withUrl = newList.map((f) => ({
      ...f,
      url: f.url || f.thumbUrl || (f.originFileObj ? URL.createObjectURL(f.originFileObj) : undefined),
    }));
    setMediaList(withUrl);
    
    // Store original images for filter reversion
    withUrl.forEach((item, index) => {
      if (item.url && item.type?.startsWith('image') && !originalImageMap[index]) {
        setOriginalImageMap(prev => ({
          ...prev,
          [index]: item.url
        }));
      }
    });
    
    if (withUrl.length && (selectedIndex == null || selectedIndex >= withUrl.length)) {
      setSelectedIndex(0);
    }
  }

  async function onFinish(values) {
    setSubmitting(true);
    try {
      const payload = {
        ...values,
        media: mediaList.map((m) => m.originFileObj || m.url).filter(Boolean),
        scheduledTime: values.scheduledTime?.toISOString?.(),
      };
      if (typeof createPost === 'function') {
        await createPost(payload);
      }
      messageApi.success('Saved');
    } catch (err) {
      console.error(err);
      messageApi.error('Save failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleAI() {
    try {
      const content = form.getFieldValue('content') || '';
      let aiText = content;
      if (typeof generateCaption === 'function') {
        const res = await generateCaption(content);
        aiText = res?.data?.caption || res?.caption || content;
      }
      form.setFieldsValue({ content: aiText });
    } catch (e) {
      console.error(e);
      messageApi.error('AI generate failed');
    }
  }

  function onVideoLoaded() {
    const v = videoRef.current;
    if (!v) return;
    const d = Math.max(0, Math.floor(v.duration || 0));
    setDuration(d);
    setTrimRange([0, Math.min(10, d || 5)]);
  }

  const applyVideoEdit = async () => {
    if (!isVideo || selectedIndex == null) return;
    setProcessing(true);
    try {
      const ffmpeg = await loadFFmpeg();
      const inputName = 'input.mp4';
      const outputName = 'output.mp4';
      const audioName = 'music.mp3';

      const file = selectedItem.originFileObj;
      await ffmpeg.writeFile(inputName, await fetchFile(file));
      if (audioFile) await ffmpeg.writeFile(audioName, await fetchFile(audioFile));

      const [start, end] = trimRange;
      const args = ['-ss', String(start), '-to', String(end), '-i', inputName];

      if (audioFile) {
        args.push('-i', audioName);
      }

      // Video filters: scale + speed
      const vf = [];
      if (resolution !== 'auto') {
        const map = {
          '1080x1920': '1080:1920',
          '1080x1080': '1080:1080',
          '1280x720': '1280:720',
        };
        const scale = map[resolution];
        if (scale) vf.push(`scale=${scale}:force_original_aspect_ratio=decrease,pad=${scale}:(ow-iw)/2:(oh-ih)/2`);
      }
      if (speed && speed !== 1) {
        vf.push(`setpts=${(1 / speed).toFixed(3)}*PTS`);
      }
      if (vf.length) {
        args.push('-vf', vf.join(','));
      }

      // Audio handling
      if (audioFile) {
        args.push('-map', '0:v:0', '-map', '1:a:0');
        if (musicVolume && musicVolume !== 1) {
          args.push('-filter:a', `volume=${musicVolume}`);
        }
        args.push('-shortest');
      } else if (muteOriginal) {
        args.push('-an');
      } else if (speed && speed !== 1 && speed >= 0.5 && speed <= 2.0) {
        args.push('-filter:a', `atempo=${speed}`);
      }

      args.push(outputName);

      await ffmpeg.exec(args);
      const data = await ffmpeg.readFile(outputName);
      const blob = new Blob([data.buffer], { type: 'video/mp4' });
      const url = URL.createObjectURL(blob);
      const updated = [...mediaList];
      updated[selectedIndex] = {
        ...updated[selectedIndex],
        url,
        originFileObj: new File([blob], updated[selectedIndex].name || 'video.mp4', { type: 'video/mp4' }),
      };
      setMediaList(updated);
      messageApi.success('Video edited');
    } catch (e) {
      console.error(e);
      messageApi.error('Video edit failed');
    } finally {
      setProcessing(false);
    }
  };

  // Initialize fabric once and debug
  React.useEffect(() => { 
    console.log('Canvas init effect triggered. Current fabricCanvas:', !!fabricCanvas);
    if (!fabricCanvas) {
      console.log('Initializing canvas...');
      initCanvas();
    }
  }, [fabricCanvas, initCanvas]);

  // Load overlay when selecting different media
  useEffect(() => {
    if (!fabricCanvas) return;
    
    console.log('Loading overlay for media index:', selectedIndex);
    fabricCanvas.clear();
    
    // Reset filter when switching media
    setFilter('none');
    
    if (selectedIndex == null) return;
    
    const json = overlayMap[selectedIndex];
    if (json && json.objects && json.objects.length > 0) {
      console.log('Loading saved overlay with', json.objects.length, 'objects');
      (async () => {
        try {
          await fabricCanvas.loadFromJSON(json);
          fabricCanvas.renderAll();
          console.log('Overlay loaded successfully');
        } catch (e) {
          console.error('Error loading overlay:', e);
        }
      })();
    } else {
      console.log('No saved overlay found for this media');
    }
  }, [selectedIndex, fabricCanvas]);

  const items = useMemo(() => ([
    {
      key: 'compose',
      label: 'Compose',
      children: (
        <>
          <Form layout="vertical" form={form} onFinish={onFinish}>
            {canFieldPlatformsShow && (
              <Form.Item label="Platforms" name="platforms" rules={[{ required: true, message: 'Select at least one platform' }]}>
                <Checkbox.Group options={PLATFORM_OPTIONS} disabled={!canFieldPlatformsEdit} />
              </Form.Item>
            )}
            {canFieldContentShow && (
              <Form.Item label="Content" name="content" rules={[{ required: true, message: 'Enter content' }]}>
                <TextArea rows={5} placeholder="Write your post..." disabled={!canFieldContentEdit} />
              </Form.Item>
            )}
            {canFieldContentEdit && (
              <Button icon={<RobotOutlined />} onClick={handleAI} style={{ marginBottom: 12 }}>AI Generate</Button>
            )}
            {canFieldMediaShow && (
              <Form.Item label="Media">
                <Upload
                  accept="image/*,video/*"
                  listType="picture-card"
                  fileList={mediaList}
                  onChange={onMediaChange}
                  onPreview={(file) => window.open(file.url || file.thumbUrl, '_blank')}
                  beforeUpload={() => false}
                  disabled={!canFieldMediaEdit}
                >
                  {canFieldMediaEdit && (
                    <div>
                      <UploadOutlined /> Upload
                    </div>
                  )}
                </Upload>
              </Form.Item>
            )}
            {canFieldScheduleShow && (
              <Form.Item label="Schedule" name="scheduledTime">
                <DatePicker showTime style={{ width: '100%' }} disabledDate={(d) => d && d < dayjs().startOf('day')} disabled={!canFieldScheduleEdit} />
              </Form.Item>
            )}
            <Space>
              <Button type="primary" htmlType="submit" loading={submitting}>Save</Button>
            </Space>
          </Form>
        </>
      )
    },
    {
      key: 'edit',
      label: 'Media Editor',
      children: (
        <div>
          {mediaList.length === 0 ? (
            <Text type="secondary">Upload media in Compose tab to start editing.</Text>
          ) : (
            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <Card size="small" title="Media">
                  <Space wrap>
                    {mediaList.map((item, idx) => {
                      const isModified = originalImageMap[idx] && originalImageMap[idx] !== item.url;
                      return (
                        <Button 
                          key={item.uid || idx} 
                          type={idx === selectedIndex ? 'primary' : 'default'} 
                          onClick={() => setSelectedIndex(idx)} 
                          icon={item.type?.startsWith('video') ? <VideoCameraOutlined /> : <PictureOutlined />}
                          style={isModified ? { border: '2px solid #1890ff' } : {}}
                        >
                          {item.name || (item.type?.startsWith('video') ? 'Video' : 'Image')} {idx+1}
                          {isModified && ' *'}
                        </Button>
                      );
                    })}
                  </Space>
                </Card>
                <Card size="small" title="Aspect Ratio" style={{ marginTop: 12 }}>
                  <Radio.Group value={aspect} onChange={(e) => setAspect(e.target.value)}>
                    <Space direction="vertical">
                      <Radio value={1}>1:1 (Square)</Radio>
                      <Radio value={9/16}>9:16 (Vertical)</Radio>
                      <Radio value={16/9}>16:9 (Wide)</Radio>
                    </Space>
                  </Radio.Group>
                </Card>
                <Card size="small" title="Overlays" style={{ marginTop: 12 }}>
                  <Space wrap>
                    <Button onClick={addText}>Add Text</Button>
                    <Upload {...uploadStickerProps}>
                      <Button>Upload Sticker</Button>
                    </Upload>
                    <Button onClick={saveOverlay}>Save</Button>
                    <Button danger onClick={clearOverlays}>Clear</Button>
                    <Button onClick={() => console.log('Canvas ready:', !!fabricCanvas, 'Objects:', fabricCanvas?.getObjects().length || 0)}>Debug</Button>
                  </Space>
                  <div style={{ marginTop: 12 }}>
                    <Text type="secondary">Text Style</Text>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                      <input type="color" value={textColor} onChange={(e) => setTextColor(e.target.value)} />
                      <Slider style={{ flex: 1 }} min={12} max={72} value={textSize} onChange={setTextSize} />
                      <Button onClick={applyTextStyle}>Apply</Button>
                    </div>
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <Text>Editing Mode</Text>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                      <Switch checked={overlayEditMode} onChange={setOverlayEditMode} />
                      <Text type="secondary">{overlayEditMode ? 'Overlay edit (drag/resize text & stickers)' : 'Media edit (drag/zoom image/video)'}</Text>
                    </div>
                  </div>
                </Card>
                {isImage && (
                  <Card size="small" title="Filters" style={{ marginTop: 12 }}>
                    <Radio.Group value={filter} onChange={(e) => setFilter(e.target.value)}>
                      <Space direction="vertical">
                        <Radio value="none">None</Radio>
                        <Radio value="grayscale(1)">Grayscale</Radio>
                        <Radio value="sepia(1)">Sepia</Radio>
                        <Radio value="contrast(1.2) saturate(1.4)">Vibrant</Radio>
                        <Radio value="brightness(1.2) contrast(1.1)">Bright</Radio>
                      </Space>
                    </Radio.Group>
                    <div style={{ marginTop: 8 }}>
                      <Space>
                        <Button onClick={applyImageFilter}>Apply Filter</Button>
                        <Button onClick={resetToOriginal} disabled={!originalImageMap[selectedIndex]}>Reset Original</Button>
                      </Space>
                    </div>
                  </Card>
                )}
                {!isImage && isVideo && (
                  <Card size="small" title="Video Options" style={{ marginTop: 12 }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div>
                        <Text>Speed</Text>
                        <Radio.Group value={speed} onChange={(e) => setSpeed(e.target.value)}>
                          <Radio value={0.5}>0.5x</Radio>
                          <Radio value={1}>1x</Radio>
                          <Radio value={1.5}>1.5x</Radio>
                          <Radio value={2}>2x</Radio>
                        </Radio.Group>
                      </div>
                      <Checkbox checked={muteOriginal} onChange={(e) => setMuteOriginal(e.target.checked)}>Mute Original Audio</Checkbox>
                      <div>
                        <Text>Music Volume</Text>
                        <Slider min={0} max={2} step={0.1} value={musicVolume} onChange={setMusicVolume} />
                      </div>
                      <div>
                        <Text>Resolution</Text>
                        <Select style={{ width: '100%' }} value={resolution} onChange={setResolution} options={[
                          { value: 'auto', label: 'Auto' },
                          { value: '1080x1920', label: '1080x1920 (Vertical 9:16)' },
                          { value: '1080x1080', label: '1080x1080 (Square 1:1)' },
                          { value: '1280x720', label: '1280x720 (Wide 16:9)' },
                        ]} />
                      </div>
                    </Space>
                  </Card>
                )}
              </Col>
              <Col xs={24} md={16}>
                <Card size="small" title={isImage ? 'Image Editor' : isVideo ? 'Video Editor' : 'Select media'}>
                  {!selectedItem ? (
                    <Text type="secondary">Select a media item</Text>
                  ) : isImage ? (
                    <div>
                      <div
                        ref={editorRef}
                        style={{ position: 'relative', width: '100%', background: '#000', aspectRatio: cssAspect }}
                      >
                        <div style={{ position: 'absolute', inset: 0 }}>
                          <Cropper
                            image={selectedItem.url}
                            crop={crop}
                            zoom={zoom}
                            aspect={aspect}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={onCropComplete}
                          />
                        </div>
                        <canvas
                          ref={canvasRef}
                          style={{ 
                            position: 'absolute', 
                            inset: 0, 
                            width: '100%', 
                            height: '100%', 
                            zIndex: 2, 
                            pointerEvents: overlayEditMode ? 'auto' : 'none',
                            cursor: overlayEditMode ? 'default' : 'grab'
                          }}
                        />
                      </div>
                      <div style={{ marginTop: 12 }}>
                        <Text>Zoom</Text>
                        <Slider min={1} max={3} step={0.1} value={zoom} onChange={setZoom} />
                        <div style={{ marginTop: 6 }}>
                          <Text type="secondary">Tip: Drag the image inside the frame to reframe.</Text>
                        </div>
                        <Button type="primary" onClick={applyImageCrop} style={{ marginTop: 8 }}>Apply Crop</Button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div
                        ref={editorRef}
                        onPointerDown={onPointerDownMedia}
                        onPointerMove={onPointerMoveMedia}
                        onPointerUp={onPointerUpMedia}
                        style={{ position: 'relative', width: '100%', background: '#000', overflow: 'hidden', aspectRatio: cssAspect }}
                      >
                        <video
                          ref={videoRef}
                          src={selectedItem.url}
                          style={{ position: 'absolute', top: '50%', left: '50%', transform: `translate(calc(-50% + ${videoOffset.x}px), calc(-50% + ${videoOffset.y}px)) scale(${videoZoom})`, transformOrigin: 'center center' }}
                          controls={overlayEditMode}
                        />
                        <canvas
                          ref={canvasRef}
                          style={{ 
                            position: 'absolute', 
                            inset: 0, 
                            width: '100%', 
                            height: '100%', 
                            zIndex: 2, 
                            pointerEvents: overlayEditMode ? 'auto' : 'none',
                            cursor: overlayEditMode ? 'default' : 'grab'
                          }}
                        />
                      </div>
                      <div style={{ marginTop: 12 }}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <Text>Trim</Text>
                          <Slider range min={0} max={Math.max(5, duration)} value={trimRange} onChange={(v) => setTrimRange(v)} />
                          <div>
                            <Text>Zoom</Text>
                            <Slider min={1} max={3} step={0.1} value={videoZoom} onChange={setVideoZoom} />
                            <div style={{ marginTop: 6 }}>
                              <Text type="secondary">Tip: Turn off Overlay edit to drag the video inside the frame.</Text>
                            </div>
                          </div>
                          <Text>Music (optional)</Text>
                          <Upload
                            accept="audio/*"
                            maxCount={1}
                            beforeUpload={(file) => { setAudioFile(file); messageApi.success('Music selected'); return false; }}
                          >
                            <Button icon={<SoundOutlined />}>Add Music</Button>
                          </Upload>
                          <Button type="primary" icon={<ScissorOutlined />} onClick={applyVideoEdit} disabled={processing}>
                            {processing ? <Spin size="small" /> : 'Apply Edit'}
                          </Button>
                        </Space>
                      </div>
                    </div>
                  )}
                </Card>
              </Col>
            </Row>
          )}
        </div>
      )
    }
  ]), [mediaList, selectedIndex, isImage, isVideo, zoom, aspect, trimRange, duration, processing, fabricCanvas, filter, textColor, textSize, speed, muteOriginal, musicVolume, resolution]);

  return (
    <Card>
      {contextHolder}
      <Title level={3}>Post Composer</Title>
      <Tabs items={items} />
    </Card>
  );
};

export default PostComposer;
